import Cookie from 'js-cookie';
import { GA4Utilities, SiteUtilities, LocationUtilities } from '../utils';

const AFFILIATE_PLATFORM = {
  AWIN: 'awin',
  ADMITAD: 'admitad',
  PARTNERSTACK: 'partnerstack'
};

const getAffiliateDefaultProps = () => {
  const geoLocation = LocationUtilities.getGeoLocation();
  const country = (geoLocation && geoLocation.countryName) || '';

  return ({
    pageUrl: window.location.href,
    referrer: document.referrer || '',
    gaClientId: GA4Utilities.getGAId(),
    country
  });
};

// Product-Specific Configuration
const AWIN_PARAMS = {
  fdesk: {
    id: 18172,
    cookieKey: 'fw_aw_fdesk',
    existingCookies: [
      '_aw_m_18172',
      '_aw_sn_18172'
    ],
    name: 'Freshdesk'
  },
  fservice: {
    id: 18173,
    cookieKey: 'fw_aw_fservice',
    existingCookies: [
      '_aw_m_18173',
      '_aw_sn_18173'
    ],
    name: 'Freshservice'
  }
};

const AWIN_SOURCE_IDENTIFIER = 'awc';

// Function to validate the publisher key
const validatePublisherKey = (publisherKey, id) => publisherKey.startsWith(`${id}_`);

const getCurrentRootDomain = () => {
  const domainParts = window.location.hostname.split('.');

  return domainParts.slice(domainParts.length - 2).join('.');
};

// Function to delete the product-specific cookies
const deleteProductCookies = (cookieKey) => Cookie.remove(cookieKey, { path: '/', domain: `.${getCurrentRootDomain()}` });

const isAllowTargetingCookies = () => !!(window.OptanonActiveGroups && window.OptanonActiveGroups.includes('C0004'));

const setCookieIfNeeded = (cookieKey, value) => {
  const existingValue = Cookie.get(cookieKey);

  if (existingValue === value) {
    return;
  }

  Cookie.set(cookieKey, value, {
    expires: 90,
    secure: true,
    path: '/'
  });
};

const awinInit = () => {
  if (!isAllowTargetingCookies()) {
    return;
  }

  const publisherKey = SiteUtilities.getParameterByName(AWIN_SOURCE_IDENTIFIER);
  if (!publisherKey) {
    return;
  }

  // Loop through the products and find the matching one for the publisher key
  const matchingProduct = Object.keys(AWIN_PARAMS).map(key => AWIN_PARAMS[key]).find(({ id }) => validatePublisherKey(publisherKey, id));

  if (!matchingProduct) {
    return;
  }

  // Delete the previously set cookies via GTM method
  matchingProduct.existingCookies.forEach(deleteProductCookies);
  setCookieIfNeeded(matchingProduct.cookieKey, publisherKey);
};

const getAwinConversionPayload = (defaultPayload) => {
  const { product: productKey, email, accountId: orderReference } = defaultPayload;

  // Fetch product parameters
  const productParams = AWIN_PARAMS[productKey];
  if (!productParams) {
    return null;
  }

  let advertiserId = productParams.id;
  let publisherKey;
  let source;

  // Helper function to find publisher key
  const findPublisherKey = (cookieKeys) => (
    cookieKeys.find((cookieKey) => {
      const value = Cookie.get(cookieKey);
      if (value) {
        publisherKey = value;
        return true;
      }

      return false;
    })
  );

  // First attempt to find publisher key for the current product
  source = findPublisherKey([productParams.cookieKey, ...productParams.existingCookies]);

  // If no publisher key, check other products
  if (!publisherKey) {
    Object.keys(AWIN_PARAMS).some((key) => {
      const params = AWIN_PARAMS[key];
      if (params.id === productParams.id) {
        return false; // Skip the current product's params
      }
      source = findPublisherKey([params.cookieKey, ...params.existingCookies]);
      if (publisherKey) {
        advertiserId = params.id;
      }

      return !!publisherKey;
    });
  }

  if (!publisherKey) {
    return null;
  }

  const defaultProps = getAffiliateDefaultProps();
  return Object.assign({
    orderReference: `${orderReference}` || productKey,
    advertiserId,
    publisherKey,
    source,
    productName: productParams.name,
    productId: productKey,
    email,
    platform: AFFILIATE_PLATFORM.AWIN
  }, defaultProps);
};

const init = () => {
  awinInit();
};

window.addEventListener('OneTrustGroupsUpdated', event => init());

export {
  init as affiliateMarketingScriptInit,
  getAwinConversionPayload
};
